/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Header, LoneIconButton, Tag } from '@blend/enterprise-components';
import { cardWidth, wideCardWidth } from 'blb-styles/constants';

export const styles = StyleSheet.create({
  headerBarContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    maxWidth: cardWidth,
  },
  headerContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 16,
  },
  sectionContentContainer: {
    marginBottom: 48,
  },
  title: {
    marginRight: '8px',
  },
  wideHeaderBarContainer: {
    maxWidth: wideCardWidth,
  },
});

export namespace DashboardSectionItem {
  export interface SectionConfig {
    content: React.ReactNode;
    hideTitle?: boolean;
    pill?: PillConfig;
    title: string;
    // if true, shows an icon to allow expanding /collapsing the section
    collapsible?: boolean;
  }

  interface PillConfig {
    text: string;
    theme: Tag.Theme;
  }

  export interface Props {
    sectionConfig: SectionConfig;
  }
}

export const DashboardSectionItem: React.FC<DashboardSectionItem.Props> = ({ sectionConfig }) => {
  const isSectionExpandedPrefix = 'config.isSectionExpanded';
  const [isExpanded, setIsExpanded] = React.useState<boolean>(
    sectionConfig.collapsible === undefined ||
      (sectionConfig.collapsible &&
        localStorage.getItem(`${isSectionExpandedPrefix}.${sectionConfig.title}`) === 'true'),
  );
  const { title, content, hideTitle, pill } = sectionConfig;
  const toggleExpanded = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    localStorage.setItem(`${isSectionExpandedPrefix}.${title}`, newExpanded.toString());
  };
  const expandIcon =
    sectionConfig.collapsible === undefined ? undefined : (
      <LoneIconButton
        btnSize="xs"
        icon={isExpanded ? 'remove' : 'add'}
        iconSize={14}
        id={`button-expand-${title}`}
        onClick={toggleExpanded}
        theme="tertiary"
      />
    );
  return (
    <div key={title}>
      {!hideTitle && (
        <div className={css(styles.headerContainer)}>
          <Header className={css(styles.title)} size={4}>
            {title}
          </Header>
          {pill && <Tag text={pill.text} theme={pill.theme} />}
          {expandIcon}
        </div>
      )}
      {isExpanded && <div className={css(styles.sectionContentContainer)}>{content}</div>}
    </div>
  );
};
